/**
 * A held row's timepoint-cell writes (`SheetRow.Handle`'s `write` of a
 * `range` or a held timepoint cell, `moveCell`, `replaceCell`, and `clear`
 * of a held timepoint cell), once the row resolved the cell its target
 * names: where each write goes (the rows-side source row's store the row is
 * painted from, else the sheet's cell writer, else posted and reconciled
 * into the row's own cache), the one move ledger a move follows, and the
 * repaint of the days a write touched.
 *
 * It reads the row only through its port (`RowTimepoints.Port`), handed to
 * it by the facade that owns it: the row's id and wire key, its writer
 * (`RowWriter`, the promote precondition), its stores and its cache. The
 * cache is opaque here (`C`): what is done to it is the port's.
 */
import type { CellEntry, CellValue, Option, RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import { CellId, Json } from "@tento-lona/sheets";
import { DateTime, NaiveDate, SemanticTime, type Utc } from "@tento-chrono";
import type { Rational } from "@tento-core/rational";
import type { ResponseError } from "@tento-core/typed-api";
import type { Result } from "@tento-core/result";
import type { SourceRow } from "@tento-lona/sheets/platform";
import { cellValueWire, type MoveWrites, type RowWriter, UtcDays } from "@tento-lona/sheets/internal-sdk";
import { Network } from "../network.ts";
import { CellWriteError, SdkRequestError } from "../errors.ts";
import { type Actor, ColumnDataChanged, RowDataChanged } from "../invalidation.ts";

/** A held row's timepoint-cell writes over its port; `C` is the row's cell cache. */
export class RowTimepoints<C> {
  readonly #port: RowTimepoints.Port<C>;

  constructor(port: RowTimepoints.Port<C>) {
    this.#port = port;
  }

  /** The row's one writer: its timepoint writes go through `cell` (the promote first). */
  get #writer(): Pick<RowWriter, "cell"> {
    return this.#port.route.writer;
  }

  /**
   * Move (`replacement: null`: the cell's own value) or replace the held
   * timepoint cell `cell` to `to` (`SheetRow.Handle.moveCell` /
   * `replaceCell`). Paints first: through the rows-side store the row is
   * painted from, else the one move ledger (`MoveWrites`) over the row's
   * cache; a refusal takes back this move's own paint, announced as the
   * SDK's correction, and the write's `CellWriteError` is rethrown.
   */
  async relocate(
    cell: CellEntry.Timepoint,
    to: DateTime.Range<Utc>,
    replacement: Option<{ readonly data: Json }>,
    actor: Actor,
    verb: "move" | "replace",
  ): Promise<void> {
    const port = this.#port;
    const value = replacement != null ? replacement.data : cell.value;
    const source = port.route.sourceWriter();
    if (source != null) {
      // The rows-side store the row is painted from: it paints the move
      // at once (one write of the cell's lane) and the replace write —
      // sent once the sheet's promote landed — settles it: a refusal
      // takes back this move's own paint (the cell shows the newest of its
      // writes still waiting, or where it was).
      const moved = source.moveTimepointCell(cell.dtr, to, CellId.of(cell.id), value, actor);
      this.#announceTimepoints([cell.dtr, to], actor);
      try {
        await sourceCellWrite("write-timepoint-cell", moved);
      } catch (err) {
        this.#announceTimepoints([cell.dtr, to], port.correction);
        // No verdict (or one the SDK could not read): the replace may
        // have landed — the source row re-reads the days it touched.
        port.failures.afterFailedWrite(err, `row.${verb}`, () => source.reread(spanOf(cell.dtr, to)));
        throw err;
      }
      return;
    }
    // The row's cells live in the SDK's own cell cache: the one move
    // ledger (`MoveWrites`) orders its moves, shared by every facade over
    // the backing row.
    const rh = port.ledger.cache();
    const moves = port.ledger.moves();
    const { move, show } = moves.begin(cell.id, cell.dtr, to);
    if (rh != null) port.ledger.showMove(rh, show?.from ?? cell.dtr, to, cell.id, value);
    this.#announceTimepoints([cell.dtr, to], actor);
    try {
      await moves.send(move, () => this.#sendTimepoint(to, value, CellId.of(cell.id), actor));
    } catch (err) {
      const back = moves.refuse(move, err);
      if (back != null) {
        if (rh != null) port.ledger.takeBackMove(rh, cell, back);
        this.#announceTimepoints([back.from, back.to], port.correction);
      }
      // No verdict (or one the SDK could not read): the replace may have
      // landed — the take-back is not the server's state, so the row's
      // cells are marked stale and re-read.
      port.failures.afterFailedWrite(err, `row.${verb}`, () => {
        port.failures.markCellsStale();
        this.#announceTimepoints([cell.dtr, to], port.correction);
        return undefined;
      });
      throw err;
    }
    moves.accept(move);
  }

  /**
   * A timepoint write of `range` (`replaceCellId` requests the server's
   * atomic replace), then the repaint of its days stamped with `actor`:
   * through the rows-side source row's store the row is painted from,
   * else the sheet's cell writer, else — a standalone row — posted and
   * reconciled into the row's cache. The promote comes first on every
   * path (`RowWriter.cell`): an ephemeral sheet's timepoint write never
   * reaches the server under the row's pre-promotion id. A failure that
   * may have stored re-reads (`afterFailedWrite`).
   */
  async writeTimepoint(
    range: DateTime.Range<Utc>,
    data: Json | CellValue,
    replaceCellId: Option<CellId>,
    actor: Actor,
  ): Promise<void> {
    const port = this.#port;
    const source = port.route.sourceWriter();
    if (source != null) {
      try {
        await sourceCellWrite(
          "write-timepoint-cell",
          this.#writer.cell(() => source.writeTimepointCell(range, data, replaceCellId, actor)),
        );
      } catch (err) {
        // Nothing reconciled; a write that may have stored is re-read.
        port.failures.afterFailedWrite(err, "row.write", () => source.reread(range));
        throw err;
      }
      this.#announceTimepoints([range], actor);
      return;
    }
    if (port.route.sheetCells() != null) {
      await this.#sendTimepoint(range, data, replaceCellId, actor);
      return;
    }
    try {
      await this.#postTimepoint(range, data, replaceCellId, actor);
    } catch (err) {
      port.failures.afterFailedWrite(err, "row.write", () => port.failures.markCellsStale());
      throw err;
    }
  }

  /**
   * Send a timepoint write of `range` for a row whose cells the SDK's own
   * cache holds, with no recovery (the caller's): through the sheet's cell
   * writer (its promote, cache and cascade; a failure marks the row stale),
   * else posted (`#postTimepoint`). Repaints its days stamped with `actor`.
   */
  async #sendTimepoint(
    range: DateTime.Range<Utc>,
    data: Json | CellValue,
    replaceCellId: Option<CellId>,
    actor: Actor,
  ): Promise<void> {
    const cells = this.#port.route.sheetCells();
    if (cells == null) return await this.#postTimepoint(range, data, replaceCellId, actor);
    await cells.setCustom(this.#port.row.cellKey(), range, data, replaceCellId, this.#port.row.position(), actor);
    this.#announceTimepoints([range], actor);
  }

  /**
   * Post a timepoint cell spanning `range` for a row whose cells the SDK's
   * own cache holds (`replaceCellId` requests the server's atomic replace:
   * the old cell is deleted before the insert), reconcile the server's
   * answer into the row's cache, repaint its days stamped with `actor`. A
   * failure rejects with nothing reconciled; the caller recovers
   * (`writeTimepoint`, `relocate`).
   */
  async #postTimepoint(
    range: DateTime.Range<Utc>,
    data: Json | CellValue,
    replaceCellId: Option<CellId>,
    actor: Actor,
  ): Promise<void> {
    // Wire boundary: the rows-side handlers read `replaceCellId` out of
    // the `data` payload, so it is folded in HERE — the same contract
    // `RowsHttpBackend.writeTimepointCell` implements (`cellValueWire`:
    // a canonical event's `null` extras left out).
    const wire = cellValueWire(data);
    const wireData = replaceCellId != null
      ? { ...(Json.object(wire) ?? {}), replaceCellId }
      : wire;
    const answer = await this.#writer.cell((rowKey) =>
      // after-failed-write: the callers recover — `writeTimepoint`, and `relocate` after its take-back.
      CellWriteError.check(
        "write-timepoint-cell",
        "POST",
        "/api/rows/{rowKey}/cells",
        () =>
          this.#port.route.postCell(
            rowKey,
            Network.CellWriteRequest.make({
              date: SemanticTime.fromDateTime(range.start),
              endDate: SemanticTime.fromDateTime(range.end),
              data: wireData,
              id: null,
            }),
          ),
      )
    );
    const rh = this.#port.ledger.cache();
    if (rh != null) this.#port.ledger.reconcile(rh, replaceCellId, answer.cells, actor);
    this.#announceTimepoints([range], actor);
  }

  /** Delete a held timepoint cell by its id and repaint its days: through
   *  the rows-side store, else the sheet's cell writer, else posted
   *  (`id` + empty data is the row's delete) and reconciled. */
  async deleteTimepoint(cell: { readonly id: CellId }, actor: Actor): Promise<void> {
    const port = this.#port;
    const source = port.route.sourceWriter();
    if (source != null) {
      // Repaint the days the row holds it on (none when it holds no
      // such id: the delete is by id, the server's to decide).
      const shown = source.row.peekCells(null).find((c) => c.kind === "timepoint" && c.id === cell.id);
      try {
        await sourceCellWrite(
          "clear-timepoint-cell",
          this.#writer.cell(() => source.clearTimepointCell(CellId.of(cell.id), actor)),
        );
      } catch (err) {
        // The source row took its delete back; one that may have
        // stored is re-read over the days the cell was on.
        port.failures.afterFailedWrite(err, "row.clear", () =>
          shown?.kind === "timepoint" ? source.reread(shown.dtr) : undefined);
        throw err;
      }
      this.#announceTimepoints(shown?.kind === "timepoint" ? [shown.dtr] : [], actor);
      return;
    }
    const cached = port.ledger.cache();
    const held = cached == null ? null : port.ledger.peekTimepoint(cached, cell.id);
    const cells = port.route.sheetCells();
    if (cells != null) {
      await cells.deleteCustom(port.row.cellKey(), CellId.of(cell.id), actor);
      this.#announceTimepoints(held == null ? [] : [held.dtr], actor);
      return;
    }
    let answer: Network.CellWriteResponse;
    try {
      answer = await this.#writer.cell((rowKey) =>
        CellWriteError.check(
          "clear-timepoint-cell",
          "POST",
          "/api/rows/{rowKey}/cells",
          () =>
            this.#port.route.postCell(
              rowKey,
              Network.CellWriteRequest.make({ id: CellId.of(cell.id), data: null, date: null, endDate: null }),
            ),
        )
      );
    } catch (err) {
      port.failures.afterFailedWrite(err, "row.clear", () => port.failures.markCellsStale());
      throw err;
    }
    const rh = port.ledger.cache();
    if (rh != null) port.ledger.reconcile(rh, cell.id, answer.cells, actor);
    this.#announceTimepoints(held == null ? [] : [held.dtr], actor);
  }

  /**
   * Repaint after a timepoint-cell write. Timepoint cells are painted
   * by PROJECTIONS (calendar day columns), not by this row's own
   * cells — a row-scoped rebind alone leaves the spans stale. Emit
   * a column-content invalidation per UTC day a range touches, by the
   * one rule (`UtcDays.touched`: a range ending at a UTC midnight does
   * not touch that date), each carrying the instants of the ranges on
   * that day (their hull when two share it, as a move inside one UTC
   * date does), in which a viewer's paint surface finds its own day
   * columns; plus a row rebind for any row-owned UI.
   */
  #announceTimepoints(
    ranges: readonly DateTime.Range<Utc>[],
    actor: Actor,
  ): void {
    const announce = (event: ColumnDataChanged | RowDataChanged) => this.#port.announce(event);
    const id = this.#port.row.id();
    // Per UTC day, in first-seen order: the day and the hull of the ranges on it.
    const days = new Map<number, { day: NaiveDate; instants: DateTime.Range<Utc> }>();
    for (const r of ranges) {
      for (const day of UtcDays.touched(r).iter()) {
        const held = days.get(day.dse)?.instants;
        days.set(day.dse, {
          day,
          instants: held == null ? r : new DateTime.Range(
            held.start.mse <= r.start.mse ? held.start : r.start,
            held.end.mse >= r.end.mse ? held.end : r.end,
          ),
        });
      }
    }
    for (const { day, instants } of days.values()) {
      announce(ColumnDataChanged.timed(actor, NaiveDate.Partial.fromDate(day), id, instants));
    }
    announce(new RowDataChanged(actor, id, null));
  }
}

export namespace RowTimepoints {
  /**
   * What the timepoint writes read of their row (`SheetRow.Handle`), by role,
   * each member answered from the row's state when it is asked: the row
   * itself, the route a cell write takes, the move ledger over the row's
   * cache (`C`, opaque here: every operation on it is the ledger's), how a
   * failed write recovers, and the repaint announcement. No role hands out
   * the client's API or its invalidation sink.
   */
  export interface Port<C> {
    /** The actor a take-back is announced as (`SheetRow.Handle.ACTOR`). */
    readonly correction: Actor;
    readonly row: RowIdentity;
    readonly route: CellRoute;
    readonly ledger: MoveLedger<C>;
    readonly failures: Failures;
    /** Announce a repaint: the row's, and each day column a timepoint spans. */
    announce(event: ColumnDataChanged | RowDataChanged): void;
  }

  /** The row a timepoint write addresses. */
  export interface RowIdentity {
    /** The row's id (`RowLocalId`) its repaints name. */
    id(): RowLocalId;
    /** The row's wire key for a write through the sheet's cell writer. */
    cellKey(): RowNetworkKey;
    /** Where the row sits in its sheet (a lookup-seeded row is persisted there). */
    position(): Option<Rational>;
  }

  /** The routes a timepoint write takes, in the order it tries them. */
  export interface CellRoute {
    /** The row's one writer; its `cell` awaits the promote first. */
    readonly writer: Pick<RowWriter, "cell">;
    /** The writers of the row's rows-side source row, or `null` (its cells are the SDK's cache only). */
    sourceWriter(): Option<SourceRow.Writer>;
    /** The sheet's cell writer, or `null` (a standalone row, a tree built without one). */
    sheetCells(): Option<SheetCells>;
    /** POST one cell write for the row keyed `rowKey` (`/api/rows/{rowKey}/cells`): the posted route's only capability. */
    postCell(rowKey: RowNetworkKey, request: Network.CellWriteRequest): Promise<Result<Network.CellWriteResponse, ResponseError>>;
  }

  /** The move ledger over the row's cell cache `C`, and the cache's reads and repairs. */
  export interface MoveLedger<C> {
    /** The timepoint-cell moves in flight on the backing row (one ledger per backing row). */
    moves(): MoveWrites<string, DateTime.Range<Utc>>;
    /** The row's cell cache, or `null` when it has none. */
    cache(): Option<C>;
    /** Paint a move of cell `id` (holding `value`) from `from` to `to` on `cache`. */
    showMove(cache: C, from: DateTime.Range<Utc>, to: DateTime.Range<Utc>, id: CellId, value: Json | CellValue): void;
    /** Take a refused move of `cell` back on `cache` (`takeBackMove`). */
    takeBackMove(cache: C, cell: CellEntry.Timepoint, back: MoveWrites.Shown<DateTime.Range<Utc>>): void;
    /** The timepoint cell `id` as `cache` holds it now. */
    peekTimepoint(cache: C, id: CellId): Option<CellEntry.Timepoint>;
    /** Reconcile the server's answer to a timepoint write into `cache`. */
    reconcile(cache: C, removeId: Option<string>, cells: readonly Network.SheetCell[], actor: Actor): void;
  }

  /** How a failed write recovers. */
  export interface Failures {
    /** A failed write's recovery under the row's owners (`afterFailedWrite`). */
    afterFailedWrite(err: unknown, verb: string, recover: () => Promise<void> | undefined): void;
    /** Mark the row's cells stale and tell its readers (as the SDK). */
    markCellsStale(): undefined;
  }

  /** The two timepoint writes of the sheet's cell writer the row sends through. */
  export interface SheetCells {
    setCustom(
      rowId: RowNetworkKey,
      dateRange: DateTime.Range<Utc>,
      data: Json | CellValue,
      replaceCellId: Option<CellId>,
      position: Option<Rational>,
      actor: Actor,
    ): Promise<void>;
    deleteCustom(rowId: RowNetworkKey, cellId: CellId, actor: Actor): Promise<void>;
  }
}

/** The range from the earlier start to the later end of `a` and `b`. */
export function spanOf(a: DateTime.Range<Utc>, b: DateTime.Range<Utc>): DateTime.Range<Utc> {
  return new DateTime.Range(
    a.start.date.dse <= b.start.date.dse ? a.start : b.start,
    a.end.date.dse >= b.end.date.dse ? a.end : b.end,
  );
}

/**
 * A timepoint write through the row's rows-side source row, refused as the
 * row's own cell-store write is: a request's failure below the cells
 * boundary (the rows backend's `RowsHttpBackendRequestError`) is re-reported as
 * a `CellWriteError` of `step`, keeping the request's method, path,
 * outcome, reason and details and the failure as `cause`. An error that is
 * not a request's failure (a disposed sheet, a bug) is rethrown as is.
 */
export async function sourceCellWrite(step: CellWriteError.Step, write: Promise<unknown>): Promise<void> {
  try {
    await write;
  } catch (err) {
    if (!(err instanceof SdkRequestError) || err instanceof CellWriteError) throw err;
    const wrapped = new CellWriteError({
      step,
      method: err.method,
      path: err.path,
      failure: err.failure,
      ...(err.reason != null ? { reason: err.reason } : {}),
      details: err.details,
    });
    wrapped.cause = err;
    throw wrapped;
  }
}
